import React, { useState, useRef, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { MdHelpOutline } from 'react-icons/md';

import CrmSelectField from '../../../UI/Crm/Fields/CrmSelectField';
import CrmButton from '../../../UI/Crm/CrmButton/CrmButton';
import { ExcelRenderer } from 'react-excel-renderer';
import { useSelector } from 'react-redux';
import Spinner from '../../../UI/Spinner/Spinner';
import validator from 'validator';
import AlertContext from '../../../../context/AlertModelContext/AlertModalContext';
import { insertBaseRecord, getBaseRecordsByModuleId, patchBaseRecord, linkRelatedRecords } from '../../../../axios/requests/baseRecordReq'

// TODO: Replace with a real call to fetch system fields from backend
// const SYSTEM_FIELDS = [
//     { value: 'primaryPhone', label: 'טלפון ראשי' },
//     { value: 'email', label: 'אימייל' },
//     { value: 'firstName', label: 'שם פרטי' },
//     { value: 'lastName', label: 'שם משפחה' },
// ];

const ModuleImportRecords = ({
    onCancelClicked,
    onReloadGrid
}) => {
    const [t, i18n] = useTranslation('crm');
    const alertCtx = useContext(AlertContext);
    const isRTL = i18n.language === 'heb';
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [columns, setColumns] = useState([]); // Excel/CSV headers
    const [mapping, setMapping] = useState({}); // {header: systemField}
    const [importMode, setImportMode] = useState('create'); // 'create' | 'update'
    const [identifier, setIdentifier] = useState({
        fileField: '',
        systemField: '',
    });

    // Options for create/update modes
    const [updateAndInsertMissing, setUpdateAndInsertMissing] = useState(false); // Update mode: insert if not found
    const [skipDuplicates, setSkipDuplicates] = useState(false); // Create mode: skip if exists
    const [systemFields, setSystemFields] = useState([]);

    const [rows, setRows] = useState([]);
    const [fieldDefs, setFieldDefs] = useState({});
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    // Available reference fields from module schema
    const [referenceFields, setReferenceFields] = useState([]);

    // Reference field linking configuration: { fieldName: { matchField: 'email' } }
    // Note: fileColumn is stored in referenceFields array from mapping
    const [referenceFieldConfigs, setReferenceFieldConfigs] = useState({});

    // Available MANY_TO_MANY relationships
    const [manyToManyRelations, setManyToManyRelations] = useState([]);

    // Show/hide related modules linking section
    const [showRelatedModules, setShowRelatedModules] = useState(false);

    // MANY_TO_MANY relationship linking: [{ relationshipName, moduleId, enabled, fileColumn, mainField, targetField }]
    const [manyToManyConfigs, setManyToManyConfigs] = useState([]);

    // Help modal state
    const [helpModalOpen, setHelpModalOpen] = useState(null); // 'fileSelection', 'importMode', 'columnMapping', 'importOptions', 'relatedModules'

    const delay = (ms) => new Promise((res) => setTimeout(res, ms));

    const crmState = useSelector(state => state.crm)
    const selectedCrmModule = crmState?.selectedCrmModule

    const columnOptionsWithEmpty = [
        { value: '', label: `- ${t('Unmapped')} -` },
        ...columns.map((c) => ({ value: c, label: c })),
    ];

    const systemFieldOptionsWithEmpty = [
        { value: '', label: `- ${t('Unmapped')} -` },
        ...systemFields,
    ];

    const getSystemOptionsForColumn = (col) => {
        const used = Object.entries(mapping)
            .filter(([c]) => c !== col)      // כל עמודות אחרות
            .map(([, v]) => v)               // הערכים שכבר נבחרו
            .filter(Boolean);

        return [
            { value: '', label: `- ${t('Unmapped')} -` },
            ...systemFields.filter(
                (f) => f.value === mapping[col] || !used.includes(f.value)
            ),
        ];
    };

    useEffect(() => {
        if (!selectedCrmModule?.sections?.length) {
            setSystemFields([]);
            setFieldDefs({});
            return;
        }

        const orderedSections = JSON.parse(JSON.stringify(selectedCrmModule.sections))
            .filter(sec => sec.order)
            .sort((a, b) => a.order - b.order)
            .map(sec => ({
                ...sec,
                fields: [...sec.fields]
                    .filter(f => f.order)
                    .sort((a, b) => a.order - b.order)
            }));

        const sf = [];
        const defs = {};

        orderedSections.forEach(sec => {
            sec.fields.forEach(fld => {
                // Add ALL fields to fieldDefs (including reference fields for linking detection)
                if (!['createdAt', 'updatedAt'].includes(fld.name)) {
                    defs[fld.name] = fld;
                }

                // Only show importable OR required fields in the dropdown
                if (!fld.importable && !fld.required) return;
                if (['createdAt', 'updatedAt'].includes(fld.name)) return;

                const label = fld.label || fld.name;
                const labelWithRequired = fld.required ? `${label} *` : label;
                sf.push({ value: fld.name, label: labelWithRequired });
            });
        });

        setSystemFields(sf);
        setFieldDefs(defs);
    }, [selectedCrmModule]);

    useEffect(() => {
        if (importMode !== 'update') return;
        if (columns.length === 0 || systemFields.length === 0) return;

        setIdentifier((prev) => ({
            fileField: prev.fileField || columns[0],
            systemField: prev.systemField || systemFields[0]?.value,
        }));
    }, [importMode, columns, systemFields]);

    // Automatically map the identifier field when it's selected
    useEffect(() => {
        if (importMode !== 'update') return;
        if (!identifier.fileField || !identifier.systemField) return;

        // Auto-map the identifier field in the mapping section
        setMapping((prev) => ({
            ...prev,
            [identifier.fileField]: identifier.systemField
        }));
    }, [importMode, identifier.fileField, identifier.systemField]);

    // Detect reference fields from mapping
    useEffect(() => {
        if (!mapping || Object.keys(mapping).length === 0) {
            setReferenceFields([]);
            return;
        }

        const refFields = [];
        Object.entries(mapping).forEach(([fileColumn, systemFieldName]) => {
            if (!systemFieldName) return;
            const fieldDef = fieldDefs[systemFieldName];

            if (fieldDef?.type === 'reference' && fieldDef.refModelId) {
                refFields.push({
                    fieldName: systemFieldName,
                    fileColumn,
                    refModule: fieldDef.refModelId,
                    displayField: fieldDef.displayField,
                    label: fieldDef.label || systemFieldName
                });
            }
        });

        setReferenceFields(refFields);
    }, [mapping, fieldDefs]);

    // Detect MANY_TO_MANY relationships and ONE_TO_MANY (isPrimary: false) from module schema
    useEffect(() => {
        if (!selectedCrmModule?.relatedModules) {
            setManyToManyRelations([]);
            return;
        }

        const relations = selectedCrmModule.relatedModules
            .filter(rel =>
                rel.relationShipType === 'MANY_TO_MANY' ||
                (rel.relationShipType === 'ONE_TO_MANY' && rel.isPrimary === false)
            )
            .map(rel => ({
                name: rel.name,
                label: rel.label || rel.name,
                moduleId: rel.moduleId,
                relationShipName: rel.relationShipName,
                relationShipType: rel.relationShipType
            }));

        setManyToManyRelations(relations);
    }, [selectedCrmModule]);

    /**
     * Parse CSV (fallback when excel‑renderer canʼt detect columns)
     */
    const parseCsvHeaders = (csvText) => {
        const firstLine = csvText.split(/\r?\n/)[0];
        return firstLine.split(',').map((h) => h.trim());
    };

    const parseCsvRows = (txt) =>
        txt.split(/\r?\n/).slice(1).map(l => l.split(','));

    /**
     * Handle file input change & parse via react-excel-renderer
     */
    const onFileChange = (e) => {
        const selected = e.target.files && e.target.files[0];
        if (!selected) return;

        const ext = selected.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls', 'csv'].includes(ext)) {
            alertCtx.showAlert(
                t('error'),
                t('Supported files: Excel or CSV only'),
                'md'
            );
            return;
        }
        setImportResult(null);
        setFile(selected);

        if (ext === 'csv') {
            // Read CSV as text to extract header row
            const reader = new FileReader();
            reader.onload = (evt) => {
                const text = evt.target.result;
                const headerNames = parseCsvHeaders(text);
                setColumns(headerNames);
                setMapping(Object.fromEntries(headerNames.map((h) => [h, ''])));
                setRows(parseCsvRows(text));
            };
            reader.onerror = () => alertCtx.showAlert(
                t('error'),
                t('An error occurred while reading the CSV file.'),
                'md'
            );
            reader.readAsText(selected, 'utf-8');
            return;
        }

        // Excel files
        ExcelRenderer(selected, (err, resp) => {
            if (err) {
                console.error(err); // eslint-disable-line no-console
                alertCtx.showAlert(
                    t('error'),
                    t('An error occurred while reading the file.'),
                    'md'
                );
                return;
            }

            let headerNames = [];

            if (resp.rows && resp.rows.length > 0) {
                const firstRow = resp.rows[0];
                headerNames = firstRow.map((cell, idx) =>
                    (cell && cell.toString().trim()) || `Column ${idx + 1}`
                );
            }

            if (headerNames.length === 0 && resp.cols && resp.cols.length > 0) {
                headerNames = resp.cols.map((_, idx) => `Column ${idx + 1}`);
            }

            setColumns(headerNames);
            setMapping(Object.fromEntries(headerNames.map((h) => [h, ''])));
            setRows(resp.rows?.slice(1) || []);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';   // מאפס כדי שניתן לבחור שוב אותו קובץ
            }
        });
    };

    const handleMappingChange = (columnName, newValue) => {
        setMapping((prev) => ({ ...prev, [columnName]: newValue }));
    };

    const handleImportModeChange = (_, value) => {
        setImportMode(value);
    };

    const validateValue = (fieldDef, val) => {
        if (val === '' || val == null) return true;

        const type = fieldDef?.type;

        switch (type) {
            case 'email': return validator.isEmail(val + '');
            case 'number': return validator.isNumeric(val + '');
            case 'boolean': return ['true', 'false', '0', '1'].includes((val + '').toLowerCase());
            case 'date':
            case 'datetime': return !isNaN(Date.parse(val));
            case 'phone': {
                // Clean phone number before validation (trim and remove dashes)
                const cleanedPhone = String(val).trim().replace(/-/g, '');
                return validator.isMobilePhone(cleanedPhone, 'any', { strictMode: false });
            }
            case 'select': {
                // Check if value is in dropdown options
                const dropDownItems = fieldDef?.dropDownId?.dropDownItems;
                if (!dropDownItems || !Array.isArray(dropDownItems)) {
                    return true; // No dropdown items defined, allow any value
                }
                const validValues = dropDownItems
                    .filter(item => item.key !== 'Create' && item.value != null)
                    .map(item => String(item.value));
                return validValues.includes(String(val));
            }
            default: return true;
        }
    };

    /**
     * Convert date value to ISO-8601 format
     * Handles various date formats and Excel serial dates
     */
    const convertToISO8601 = (value, includeTime = false) => {
        if (!value) return '';

        // Handle Excel serial date numbers (days since 1900-01-01)
        if (typeof value === 'number') {
            const excelEpoch = new Date(Date.UTC(1900, 0, 1));
            const date = new Date(excelEpoch.getTime() + (value - 2) * 86400000); // -2 for Excel bug
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return includeTime ? date.toISOString() : `${year}-${month}-${day}`;
        }

        const strValue = String(value).trim();

        // Try to parse DD/MM/YYYY or DD-MM-YYYY format (common in Israel and Europe)
        const ddmmyyyyMatch = strValue.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
        if (ddmmyyyyMatch) {
            const day = parseInt(ddmmyyyyMatch[1], 10);
            const month = parseInt(ddmmyyyyMatch[2], 10);
            const year = parseInt(ddmmyyyyMatch[3], 10);

            // Determine if it's DD/MM/YYYY or MM/DD/YYYY
            // If day > 12, it must be DD/MM/YYYY
            // Otherwise, assume DD/MM/YYYY for Israeli format
            let actualDay, actualMonth;
            if (day > 12) {
                actualDay = day;
                actualMonth = month;
            } else {
                // Assume DD/MM/YYYY format (Israeli/European)
                actualDay = day;
                actualMonth = month;
            }

            // Create date in UTC to avoid timezone shifts
            const dateObj = new Date(Date.UTC(year, actualMonth - 1, actualDay));

            if (!isNaN(dateObj.getTime())) {
                const y = dateObj.getUTCFullYear();
                const m = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
                const d = String(dateObj.getUTCDate()).padStart(2, '0');
                return includeTime ? dateObj.toISOString() : `${y}-${m}-${d}`;
            }
        }

        // Try to parse YYYY-MM-DD format
        const yyyymmddMatch = strValue.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
        if (yyyymmddMatch) {
            const year = parseInt(yyyymmddMatch[1], 10);
            const month = parseInt(yyyymmddMatch[2], 10);
            const day = parseInt(yyyymmddMatch[3], 10);
            const dateObj = new Date(Date.UTC(year, month - 1, day));

            if (!isNaN(dateObj.getTime())) {
                const y = dateObj.getUTCFullYear();
                const m = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
                const d = String(dateObj.getUTCDate()).padStart(2, '0');
                return includeTime ? dateObj.toISOString() : `${y}-${m}-${d}`;
            }
        }

        // Fallback: try native Date parser
        const dateObj = new Date(value);
        if (!isNaN(dateObj.getTime())) {
            const y = dateObj.getUTCFullYear();
            const m = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const d = String(dateObj.getUTCDate()).padStart(2, '0');
            return includeTime ? dateObj.toISOString() : `${y}-${m}-${d}`;
        }

        // If all parsing fails, return original value
        return value;
    };

    const validateAll = () => {
        const globalErrors = [];
        const rowErrors = {}; // { rowIndex: [errors] }

        // 1. מיפוי לא חוקי / לא importable
        Object.entries(mapping).forEach(([col, fldName]) => {
            if (!fldName) return;
            const def = fieldDefs[fldName];
            if (!def) globalErrors.push(t(`Field '{{f}}' is not part of module`, { f: fldName }));
            else {
                if (!def.importable) globalErrors.push(t(`Field '{{f}}' not importable`, { f: def.label || def.name }));
            }
        });

        // 2. כל required חייב להיות ממופה
        Object.values(fieldDefs).filter(f => f.required)
            .forEach(req => {
                if (!Object.values(mapping).includes(req.name))
                    globalErrors.push(t(`Required field '{{f}}'' is not mapped`, { f: req.label || req.name }));
            });

        // 3. Validate reference fields have match field configured
        Object.entries(mapping).forEach(([col, fldName]) => {
            if (!fldName) return;
            const def = fieldDefs[fldName];
            if (def?.type === 'reference') {
                const config = referenceFieldConfigs[fldName];
                if (!config?.matchField) {
                    globalErrors.push(t(`Reference field '{{f}}' requires a match field`, { f: def.label || def.name }));
                }
            }
        });

        // 4. בדיקת ערכים בשורות
        rows.forEach((row, idx) => {
            Object.entries(mapping).forEach(([col, fldName]) => {
                if (!fldName) return;
                const def = fieldDefs[fldName];
                let cell = row[columns.indexOf(col)] ?? '';
                // Trim string values before validation
                if (typeof cell === 'string') {
                    cell = cell.trim();
                }
                if (def.required && (cell === '' || cell == null)) {
                    if (!rowErrors[idx]) rowErrors[idx] = [];
                    rowErrors[idx].push(t(`Field '{{f}}' is required`, { f: def.label || def.name }));
                }
                else if (!validateValue(def, cell)) {
                    if (!rowErrors[idx]) rowErrors[idx] = [];
                    // For dropdown fields, show available options
                    if (def.type === 'select' && def.dropDownId?.dropDownItems) {
                        const validOptions = def.dropDownId.dropDownItems
                            .filter(item => item.key !== 'Create' && item.value != null)
                            .map(item => item.key)
                            .join(', ');
                        rowErrors[idx].push(t(`Value '{{v}}' is invalid for field '{{f}}'. Valid options: {{options}}`,
                            { v: cell, f: def.label || def.name, options: validOptions }));
                    } else {
                        rowErrors[idx].push(t(`Value '{{v}}' is invalid for field '{{f}}'`,
                            { v: cell, f: def.label || def.name }));
                    }
                }
            });
        });

        // 5. Identifier (update mode or skip duplicates mode)
        if (importMode === 'update' || (importMode === 'create' && skipDuplicates)) {
            if (!identifier.fileField) globalErrors.push(t('Identifier column in file not selected'));
            const def = fieldDefs[identifier.systemField];
            if (!def) globalErrors.push(t('Identifier field not selected or invalid'));
            else {
                if (!def.importable) globalErrors.push(t('Identifier field not importable'));
                if (def.type === 'reference') globalErrors.push(t('Identifier field cannot be reference'));
            }
        }

        return { globalErrors, rowErrors };
    };

    const handleSubmit = async () => {
        setIsImporting(true);
        const { globalErrors, rowErrors } = validateAll();

        // If there are global errors (not related to specific rows), show in modal only
        if (globalErrors.length > 0) {
            setImportResult({
                success: false,
                errors: globalErrors,
                totalRows: rows.length,
                successCount: 0,
                failedCount: rows.length,
                failedRows: [] // No CSV export for global errors
            });
            setIsImporting(false);
            return;
        }

        // If there are row-specific errors, export those rows to CSV
        if (Object.keys(rowErrors).length > 0) {
            const failedRows = Object.entries(rowErrors).map(([idx, errors]) => ({
                rowIndex: parseInt(idx) + 2,
                data: rows[parseInt(idx)],
                record: {},
                error: errors.join('; ')
            }));

            setImportResult({
                success: false,
                errors: failedRows.map(fr => `Row ${fr.rowIndex}: ${fr.error}`),
                totalRows: rows.length,
                successCount: 0,
                failedCount: failedRows.length,
                failedRows // Export row-specific errors to CSV
            });
            setIsImporting(false);
            return;
        }

        const mappedRecords = rows.map((row) => {
            const record = {};

            Object.entries(mapping).forEach(([colName, systemField]) => {
                if (!systemField) return;

                const colIndex = columns.indexOf(colName);
                let value = row[colIndex] ?? '';

                // Trim string values
                if (typeof value === 'string') {
                    value = value.trim();
                }

                const def = fieldDefs[systemField];

                // Skip reference fields - they will be handled by reference field linking logic
                if (def?.type === 'reference') return;

                // Transform phone numbers
                if (def?.type === 'phone') {
                    value = String(value).replace(/-/g, '');
                    if (value && value[0] !== '0') {
                        value = '0' + value;
                    }
                }

                // Transform dates to ISO-8601 format
                if (def?.type === 'date') {
                    value = convertToISO8601(value, false);
                }

                // Transform datetime to ISO-8601 format with time
                if (def?.type === 'datetime') {
                    value = convertToISO8601(value, true);
                }

                // Skip empty string values - don't send to backend
                if (value === '') return;

                record[systemField] = value;
            });

            // In update mode, ensure identifier field is always included even if not mapped
            if (importMode === 'update' && identifier.systemField && identifier.fileField) {
                // Check if identifier field is not already mapped
                if (!record[identifier.systemField]) {
                    const identifierColIndex = columns.indexOf(identifier.fileField);
                    if (identifierColIndex !== -1) {
                        let identifierValue = row[identifierColIndex] ?? '';

                        // Trim string values
                        if (typeof identifierValue === 'string') {
                            identifierValue = identifierValue.trim();
                        }

                        // Apply transformations based on field type
                        const identifierDef = fieldDefs[identifier.systemField];
                        if (identifierDef?.type === 'phone' && identifierValue) {
                            identifierValue = String(identifierValue).replace(/-/g, '');
                            if (identifierValue[0] !== '0') {
                                identifierValue = '0' + identifierValue;
                            }
                        } else if (identifierDef?.type === 'date' && identifierValue) {
                            identifierValue = convertToISO8601(identifierValue, false);
                        } else if (identifierDef?.type === 'datetime' && identifierValue) {
                            identifierValue = convertToISO8601(identifierValue, true);
                        }

                        record[identifier.systemField] = identifierValue;
                    }
                }
            }

            return record;
        });

        /* ---------- insert/update records one-by-one ---------- */
        setProgress({ done: 0, total: mappedRecords.length });

        const failedRows = [];
        let successCount = 0;

        for (let i = 0; i < mappedRecords.length; i++) {
            const rec = mappedRecords[i];
            let savedRecordId = null;
            try {
                if (importMode === 'create') {
                    // Create mode
                    if (skipDuplicates) {
                        // Check if record already exists
                        let identifierValue = rec[identifier.systemField];

                        if (!identifierValue) {
                            throw new Error(t('Identifier value is empty in row'));
                        }

                        // Transform identifier value if it's a phone field
                        const identifierFieldDef = fieldDefs[identifier.systemField];
                        if (identifierFieldDef?.type === 'phone') {
                            identifierValue = String(identifierValue).trim();
                            identifierValue = identifierValue.replace(/[\s\-\(\)]/g, '');

                            if (identifierValue.startsWith('+972')) {
                                // Already in international format
                            } else if (identifierValue.startsWith('972')) {
                                identifierValue = '+' + identifierValue;
                            } else if (identifierValue.startsWith('0')) {
                                identifierValue = '+972' + identifierValue.substring(1);
                            } else {
                                identifierValue = '+972' + identifierValue;
                            }
                        }

                        // Search for existing record
                        const filters = [
                            { field: identifier.systemField, operator: 'equals', value: identifierValue },
                            { field: 'recordStatus', operator: 'equals', value: 'active' }
                        ];

                        const searchResult = await getBaseRecordsByModuleId(
                            selectedCrmModule._id, 1, 1, null, null, filters
                        );

                        const existingRecords = searchResult?.data?.records;

                        if (existingRecords && existingRecords.length > 0) {
                            // Record exists - skip creating but capture ID for linking
                            savedRecordId = existingRecords[0]._id;
                            // Don't increment successCount since we didn't create a new record
                        } else {
                            // Record doesn't exist - create it
                            const savedRecord = await insertBaseRecord(selectedCrmModule._id, rec);
                            savedRecordId = savedRecord?._id;
                            successCount++;
                        }
                    } else {
                        // Normal create - no duplicate check
                        const savedRecord = await insertBaseRecord(selectedCrmModule._id, rec);
                        savedRecordId = savedRecord?._id;
                        successCount++;
                    }
                } else if (importMode === 'update') {
                    // Update mode
                    let identifierValue = rec[identifier.systemField];

                    if (!identifierValue) {
                        throw new Error(t('Identifier value is empty in row'));
                    }

                    // Transform identifier value if it's a phone field
                    const identifierFieldDef = fieldDefs[identifier.systemField];
                    if (identifierFieldDef?.type === 'phone') {
                        identifierValue = String(identifierValue).trim();
                        identifierValue = identifierValue.replace(/[\s\-\(\)]/g, '');

                        if (identifierValue.startsWith('+972')) {
                            // Already in international format
                        } else if (identifierValue.startsWith('972')) {
                            identifierValue = '+' + identifierValue;
                        } else if (identifierValue.startsWith('0')) {
                            identifierValue = '+972' + identifierValue.substring(1);
                        } else {
                            identifierValue = '+972' + identifierValue;
                        }
                    }

                    // Search for the record using the identifier field
                    const filters = [
                        { field: identifier.systemField, operator: 'equals', value: identifierValue },
                        { field: 'recordStatus', operator: 'equals', value: 'active' }
                    ];

                    const searchResult = await getBaseRecordsByModuleId(
                        selectedCrmModule._id, 1, 1, null, null, filters
                    );

                    const records = searchResult?.data?.records;

                    if (!records || records.length === 0) {
                        if (updateAndInsertMissing) {
                            // Record not found but updateAndInsertMissing is enabled - create it
                            const savedRecord = await insertBaseRecord(selectedCrmModule._id, rec);
                            savedRecordId = savedRecord?._id;
                            successCount++;
                        } else {
                            // Record not found and updateAndInsertMissing is disabled - throw error
                            throw new Error(t('No record found with identifier value: {{value}}', { value: identifierValue }));
                        }
                    } else {
                        // Record found - update it
                        const existingRecord = records[0];

                    // Extract dynamic fields from the 'fields' array (just like ModuleSingleRecord.js does)
                    const dynamicFields = (existingRecord.fields || []).reduce((acc, cur) => {
                        acc[cur.name] = cur.value;
                        return acc;
                    }, {});

                    // Merge existing record with dynamic fields
                    const existingFieldValues = {
                        ...existingRecord,
                        ...dynamicFields
                    };

                    // Start with all existing field values
                    const mergedRecord = { ...existingFieldValues };

                    // Update only fields that have non-empty values in the CSV
                    Object.entries(rec).forEach(([fieldName, value]) => {
                        // Only update fields with actual values
                        if (value !== '' && value !== null && value !== undefined) {
                            mergedRecord[fieldName] = value;
                        }
                    });

                        // Update the record with merged data (existing + new values)
                        await patchBaseRecord(selectedCrmModule._id, existingRecord._id, mergedRecord);
                        savedRecordId = existingRecord._id;
                        successCount++;
                    }
                }

                // Process reference field linking (ONE_TO_MANY)
                if (savedRecordId && referenceFields.length > 0) {
                    for (const refField of referenceFields) {
                        const config = referenceFieldConfigs[refField.fieldName];
                        if (!config?.matchField) continue;

                        // Use the CSV column from the mapping (refField.fileColumn)
                        // to get the VALUE from the CSV row
                        const searchValue = rows[i][columns.indexOf(refField.fileColumn)];
                        if (!searchValue) continue;

                        try {
                            // Get the field definition to check if transformation is needed
                            const matchFieldDef = refField.refModule?.sections
                                ?.flatMap(sec => sec.fields || [])
                                .find(f => f.name === config.matchField);

                            let transformedSearchValue = searchValue;

                            // Apply transformations based on match field type
                            if (matchFieldDef?.type === 'phone') {
                                transformedSearchValue = String(searchValue).trim();
                                transformedSearchValue = transformedSearchValue.replace(/[\s\-\(\)]/g, '');

                                if (transformedSearchValue.startsWith('+972')) {
                                    // Already in international format
                                } else if (transformedSearchValue.startsWith('972')) {
                                    transformedSearchValue = '+' + transformedSearchValue;
                                } else if (transformedSearchValue.startsWith('0')) {
                                    transformedSearchValue = '+972' + transformedSearchValue.substring(1);
                                } else {
                                    transformedSearchValue = '+972' + transformedSearchValue;
                                }
                            } else if (matchFieldDef?.type === 'email') {
                                transformedSearchValue = String(searchValue).trim().toLowerCase();
                            }

                            // Fetch active records from the related module (without specific field filter)
                            // We'll filter client-side because backend filtering may be unreliable
                            const refSearchResult = await getBaseRecordsByModuleId(
                                refField.refModule._id,
                                1, 100, null, null,  // Get up to 100 active records
                                [{ field: 'recordStatus', operator: 'equals', value: 'active' }]
                            );

                            // Filter client-side for the matching record
                            let refRecord = null;
                            if (refSearchResult?.data?.records) {
                                refRecord = refSearchResult.data.records.find(record => {
                                    // Check if the match field exists at root level
                                    let recordValue = record[config.matchField];

                                    // If not at root, check in fields array
                                    if (!recordValue && record.fields && Array.isArray(record.fields)) {
                                        const field = record.fields.find(f => f.name === config.matchField);
                                        recordValue = field?.value;
                                    }

                                    if (!recordValue) return false;

                                    // Apply same transformations to the record value
                                    let transformedRecordValue = recordValue;
                                    if (matchFieldDef?.type === 'phone') {
                                        transformedRecordValue = String(recordValue).trim();
                                        transformedRecordValue = transformedRecordValue.replace(/[\s\-\(\)]/g, '');

                                        if (transformedRecordValue.startsWith('+972')) {
                                            // Already in international format
                                        } else if (transformedRecordValue.startsWith('972')) {
                                            transformedRecordValue = '+' + transformedRecordValue;
                                        } else if (transformedRecordValue.startsWith('0')) {
                                            transformedRecordValue = '+972' + transformedRecordValue.substring(1);
                                        } else {
                                            transformedRecordValue = '+972' + transformedRecordValue;
                                        }
                                    } else if (matchFieldDef?.type === 'email') {
                                        transformedRecordValue = String(recordValue).trim().toLowerCase();
                                    } else {
                                        transformedRecordValue = String(recordValue).trim();
                                    }

                                    return transformedRecordValue === transformedSearchValue;
                                });
                            }

                            if (refRecord) {
                                // Link the records using linkRelatedRecords
                                await linkRelatedRecords(
                                    savedRecordId,           // Current record ID
                                    refRecord._id,           // Reference record ID
                                    selectedCrmModule._id,   // Current module ID
                                    refField.refModule._id   // Reference module ID
                                );
                            }
                        } catch (refError) {
                            // Silently fail - linking is optional
                        }
                    }
                }

                // Process MANY_TO_MANY relationship linking
                if (savedRecordId && manyToManyConfigs.length > 0) {
                    for (const config of manyToManyConfigs) {
                        if (!config.enabled || !config.fileColumn || !config.targetField) continue;

                        const searchValue = rows[i][columns.indexOf(config.fileColumn)];
                        if (!searchValue) continue;

                        try {
                            // Search for related records
                            const relFilters = [
                                { field: config.targetField, operator: 'equals', value: searchValue },
                                { field: 'recordStatus', operator: 'equals', value: 'active' }
                            ];

                            const relSearchResult = await getBaseRecordsByModuleId(
                                config.moduleId,
                                1, 100, null, null,
                                relFilters
                            );

                            const relRecords = relSearchResult?.data?.records || [];

                            // Link each found record
                            for (const relRecord of relRecords) {
                                try {
                                    await linkRelatedRecords(
                                        savedRecordId,
                                        relRecord._id,
                                        selectedCrmModule._id,
                                        config.moduleId
                                    );
                                } catch (linkError) {
                                    console.error(`Failed to link record ${relRecord._id}:`, linkError);
                                }
                            }
                        } catch (relError) {
                            console.error(`Failed to search related records:`, relError);
                        }
                    }
                }
            } catch (e) {
                const errorMsg = e?.response?.data?.message || e?.message || t('Unknown error');
                failedRows.push({
                    rowIndex: i + 2, // +2 because row 1 is header, array starts at 0
                    data: rows[i],
                    record: rec,
                    error: errorMsg
                });
            }
            setProgress((p) => ({ ...p, done: p.done + 1 }));
            await delay(400); // 0.4 s between calls
        }

        const payload = {
            importMode,
            ...(importMode === 'update' && { identifier }),
            records: mappedRecords,
        };
        console.log(payload); // eslint-disable-line no-console

        setImportResult({
            success: failedRows.length === 0,
            successCount,
            failedCount: failedRows.length,
            failedRows,
            totalRows: mappedRecords.length
        });
        setIsImporting(false);

        if (failedRows.length === 0) {
            onReloadGrid?.()
            onCancelClicked?.()
        } else {
            onReloadGrid?.()
        }
    };

    const onCancelClickedHandler = () => {
        setFile(null);
        setColumns([]);
        setMapping({});
        setImportMode('create');
        setImportResult(null);
    };

    const exportFailedRows = () => {
        if (!importResult?.failedRows?.length) return;

        // Create CSV content with original columns + error column
        const csvHeaders = [...columns, t('Error')];
        const csvRows = [csvHeaders];

        importResult.failedRows.forEach(failedRow => {
            // Ensure row has same number of columns as headers by padding with empty strings
            const paddedRow = [];
            for (let i = 0; i < columns.length; i++) {
                paddedRow.push(failedRow.data[i] ?? '');
            }
            // Add error at the end
            paddedRow.push(failedRow.error);
            csvRows.push(paddedRow);
        });

        // Convert to CSV string
        const csvContent = csvRows
            .map(row => row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma, quote, or newline
                const cellStr = (cell ?? '').toString();
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(','))
            .join('\n');

        // Add BOM for proper Hebrew/UTF-8 support in Excel
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `failed_imports_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Help content for different sections
    const helpContent = {
        fileSelection: {
            title: t('File Selection Help'),
            content: t('file_selection_help_content')
        },
        importMode: {
            title: t('Import Mode Help'),
            content: t('import_mode_help_content')
        },
        columnMapping: {
            title: t('Column Mapping Help'),
            content: t('column_mapping_help_content')
        },
        importOptions: {
            title: t('Import Options Help'),
            content: t('import_options_help_content')
        },
        relatedModules: {
            title: t('Related Modules Help'),
            content: t('related_modules_help_content')
        }
    };

    const renderHelpIcon = (helpKey) => (
        <IconButton
            size="small"
            onClick={() => setHelpModalOpen(helpKey)}
            sx={{ ml: 0.5, p: 0.5 }}
        >
            <MdHelpOutline size={20} />
        </IconButton>
    );

    return (
        <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
            {isImporting && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(255,255,255,0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: 'background.paper',
                            px: 5,
                            py: 4,
                            borderRadius: 3,
                            boxShadow: 8,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minWidth: 260,
                        }}
                    >
                        <Spinner />

                        <Typography
                            variant="subtitle1"
                            mt={2}
                            fontWeight={600}
                            sx={{ letterSpacing: 0.5 }}
                        >
                            {t('{{done}}/{{total}} completed', {
                                done: progress.done,
                                total: progress.total,
                            })}
                        </Typography>
                    </Box>
                </Box>
            )}
            {importResult && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.25)',
                        zIndex: 9,
                        p: 2,
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 520,
                            width: '100%',
                            maxHeight: '80%',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            p: 4,
                        }}
                    >
                        <Typography
                            variant="h6"
                            color={importResult.success ? 'success.main' : 'warning.main'}
                            textAlign="center"
                            mb={2}
                        >
                            {importResult.success
                                ? t('Import completed successfully!')
                                : t('Import completed with errors')}
                        </Typography>

                        {/* Statistics */}
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {t('Total rows')}: {importResult.totalRows || 0}
                            </Typography>
                            <Typography variant="body1" color="success.main" sx={{ mb: 0.5 }}>
                                {t('Successfully imported')}: {importResult.successCount || 0}
                            </Typography>
                            {importResult.failedCount > 0 && (
                                <Typography variant="body1" color="error.main">
                                    {t('Failed to import')}: {importResult.failedCount}
                                </Typography>
                            )}
                        </Box>

                        {/* Validation errors (pre-import) */}
                        {!importResult.success && importResult.errors && (
                            <Box
                                component="ul"
                                sx={{
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    mt: 1,
                                    pr: 1,
                                    listStyle: 'disc inside',
                                }}
                            >
                                {importResult.errors.map((err, idx) => (
                                    <Typography
                                        key={idx}
                                        component="li"
                                        variant="body2"
                                        sx={{ mb: 0.5 }}
                                    >
                                        {err}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {/* Failed rows info (post-import) */}
                        {importResult.failedRows && importResult.failedRows.length > 0 && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    {t('You can export the failed rows to fix the errors and import them again.')}
                                </Typography>
                            </Box>
                        )}

                        <Box display="flex" justifyContent="center" gap={2} mt={3}>
                            {importResult.failedRows && importResult.failedRows.length > 0 && (
                                <CrmButton
                                    variant="contained"
                                    color="crmContainedGray94"
                                    onClick={exportFailedRows}
                                >
                                    {t('Export Failed Rows')}
                                </CrmButton>
                            )}
                            <CrmButton
                                variant="contained"
                                color={importResult.success ? 'success' : 'primary'}
                                onClick={() => {
                                    if (importResult.success) {
                                        onCancelClickedHandler();
                                    } else {
                                        setImportResult(null);
                                    }
                                }}
                            >
                                {importResult.success ? t('Close') : t('Back')}
                            </CrmButton>
                        </Box>
                    </Box>
                </Box>
            )}

            <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', overflowX: 'hidden' }}>
                {/* Step 1 – File selection */}
                <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                        {t('Select a file to import (supported formats: Excel or CSV)')}
                    </Typography>
                    {renderHelpIcon('fileSelection')}
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={2}
                    mb={0}
                >
                    <CrmButton
                        variant="contained"
                        color="success"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                        {file ? t('Change File') : t('Choose File')}
                    </CrmButton>
                    <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        onChange={onFileChange}
                        accept=".xls,.xlsx,.csv"
                    />
                    {file && <Typography>{file.name}</Typography>}
                </Box>

                {/* Render subsequent steps after file is selected */}
                {file && (
                    <>
                        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                        {/* Step 2 – Import mode */}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                {t('Please choose whether you want to import new records or update existing ones.')}
                            </Typography>
                            {renderHelpIcon('importMode')}
                        </Box>
                        <Box
                            display="flex"
                            justifyContent="flex-start"
                            alignItems="center"
                            gap={2}
                            mb={0}
                        >
                            <RadioGroup
                                row
                                value={importMode}
                                onChange={handleImportModeChange}
                                name="importMode"
                            >
                                <FormControlLabel
                                    value="create"
                                    control={<Radio />}
                                    label={t('New Records')}
                                    sx={{ mr: 1 }}
                                />
                                <FormControlLabel
                                    value="update"
                                    control={<Radio />}
                                    label={t('Update Existing Records')}
                                />
                            </RadioGroup>
                        </Box>


                        {/* Step 2.5 – update mode */}
                        {importMode === 'update' && (
                            <>
                                <Divider sx={{ my: 1 }} />

                                <Typography variant="subtitle1" mb={1} textAlign="left" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                    {t('Please select an identifier field to locate the records for updating.')}
                                </Typography>

                                <Grid container spacing={2} sx={{ mb: 1 }}>
                                    <Grid item xs={6} textAlign="left">
                                        <Typography fontWeight={600}>{t('Identifier Field in the File')}</Typography>
                                    </Grid>
                                    <Grid item xs={6} textAlign="left">
                                        <Typography fontWeight={600}>{t('Identifier Field in the Record')}</Typography>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <CrmSelectField
                                            value={identifier.fileField || ''}
                                            onChange={(e) =>
                                                setIdentifier((prev) => ({ ...prev, fileField: e.target.value }))
                                            }
                                            options={columnOptionsWithEmpty}
                                            placeholder={t('Select Column')}
                                            sx={{
                                                height: 35
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <CrmSelectField
                                            value={identifier.systemField || ''}
                                            onChange={(e) =>
                                                setIdentifier((prev) => ({ ...prev, systemField: e.target.value }))
                                            }
                                            options={systemFieldOptionsWithEmpty}
                                            placeholder={t('Select a Field in the System')}
                                            sx={{
                                                height: 35
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {/* Import Options - Skip Duplicates (Create Mode) */}
                        {importMode === 'create' && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography variant="subtitle1" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                        {t('Import Options')}
                                    </Typography>
                                    {renderHelpIcon('importOptions')}
                                </Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Checkbox
                                        checked={skipDuplicates}
                                        onChange={(e) => setSkipDuplicates(e.target.checked)}
                                        sx={{ p: 0, mr: 1 }}
                                    />
                                    <Typography>
                                        {t('Skip duplicate records (requires identifier)')}
                                    </Typography>
                                </Box>

                                {skipDuplicates && (
                                    <>
                                        <Typography variant="subtitle1" mb={1} textAlign="left" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                            {t('Please select an identifier field to check for duplicates.')}
                                        </Typography>

                                        <Grid container spacing={2} sx={{ mb: 1 }}>
                                            <Grid item xs={6} textAlign="left">
                                                <Typography fontWeight={600}>{t('Identifier Field in the File')}</Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="left">
                                                <Typography fontWeight={600}>{t('Identifier Field in the Record')}</Typography>
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <CrmSelectField
                                                    value={identifier.fileField || ''}
                                                    onChange={(e) =>
                                                        setIdentifier((prev) => ({ ...prev, fileField: e.target.value }))
                                                    }
                                                    options={columnOptionsWithEmpty}
                                                    placeholder={t('Select Column')}
                                                    sx={{
                                                        height: 35
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <CrmSelectField
                                                    value={identifier.systemField || ''}
                                                    onChange={(e) =>
                                                        setIdentifier((prev) => ({ ...prev, systemField: e.target.value }))
                                                    }
                                                    options={systemFieldOptionsWithEmpty}
                                                    placeholder={t('Select a Field in the System')}
                                                    sx={{
                                                        height: 35
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}

                        {/* Import Options - Insert Missing (Update Mode) */}
                        {importMode === 'update' && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography variant="subtitle1" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                        {t('Import Options')}
                                    </Typography>
                                    {renderHelpIcon('importOptions')}
                                </Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Checkbox
                                        checked={updateAndInsertMissing}
                                        onChange={(e) => setUpdateAndInsertMissing(e.target.checked)}
                                        sx={{ p: 0, mr: 1 }}
                                    />
                                    <Typography>
                                        {t('Insert records that don\'t exist')}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {/* Step 3.6 – Related Module Linking (MANY_TO_MANY and ONE_TO_MANY reverse) */}
                        {manyToManyRelations.length > 0 && (
                            <>
                                <Box display="flex" alignItems="center" mb={1} mt={1}>
                                    <Checkbox
                                        checked={showRelatedModules}
                                        onChange={(e) => setShowRelatedModules(e.target.checked)}
                                        sx={{ p: 0, mr: 1 }}
                                    />
                                    <Typography variant="subtitle1" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                        {t('Link to Related Modules - Optional')}
                                    </Typography>
                                    {renderHelpIcon('relatedModules')}
                                </Box>

                                {showRelatedModules && manyToManyRelations.map((relation) => {
                                    const config = manyToManyConfigs.find(c => c.relationshipName === relation.name) || {};
                                    const isEnabled = config.enabled || false;

                                    const targetModuleSections = relation.moduleId?.sections || [];
                                    const targetModuleFields = targetModuleSections
                                        .flatMap(sec => sec.fields || [])
                                        .filter(f => !['createdAt', 'updatedAt'].includes(f.name) && f.type !== 'reference')
                                        .map(f => ({ value: f.name, label: f.label || f.name }));

                                    return (
                                        <Box key={relation.name} sx={{ mb: 0.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Box display="flex" alignItems="center" mb={0.5}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isEnabled}
                                                            onChange={(e) => {
                                                                setManyToManyConfigs(prev => {
                                                                    const filtered = prev.filter(c => c.relationshipName !== relation.name);
                                                                    if (e.target.checked) {
                                                                        return [...filtered, {
                                                                            relationshipName: relation.name,
                                                                            moduleId: relation.moduleId._id,
                                                                            enabled: true,
                                                                            fileColumn: '',
                                                                            targetField: ''
                                                                        }];
                                                                    }
                                                                    return filtered;
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={`${t('Link to')} ${relation.label || relation.name}`}
                                                />
                                            </Box>

                                            {isEnabled && (
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body2" mb={0.5}>{t('CSV Column')}</Typography>
                                                        <CrmSelectField
                                                            value={config.fileColumn || ''}
                                                            onChange={(e) => {
                                                                setManyToManyConfigs(prev =>
                                                                    prev.map(c => c.relationshipName === relation.name
                                                                        ? { ...c, fileColumn: e.target.value }
                                                                        : c
                                                                    )
                                                                );
                                                            }}
                                                            options={columnOptionsWithEmpty}
                                                            placeholder={t('Select Column')}
                                                            sx={{ height: 35 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body2" mb={0.5}>
                                                            {t('{{module}} field', { module: relation.moduleId?.label || relation.moduleId?.name })}
                                                        </Typography>
                                                        <CrmSelectField
                                                            value={config.targetField || ''}
                                                            onChange={(e) => {
                                                                setManyToManyConfigs(prev =>
                                                                    prev.map(c => c.relationshipName === relation.name
                                                                        ? { ...c, targetField: e.target.value }
                                                                        : c
                                                                    )
                                                                );
                                                            }}
                                                            options={[
                                                                { value: '', label: `- ${t('Unmapped')} -` },
                                                                ...targetModuleFields
                                                            ]}
                                                            placeholder={t('Select Field')}
                                                            sx={{ height: 35 }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </Box>
                                    );
                                })}
                            </>
                        )}

                        {/* Step 3 – Column mapping */}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                {t('To ensure the system matches the data correctly, each column header in the file must be mapped to its corresponding field in the system.')}
                            </Typography>
                            {renderHelpIcon('columnMapping')}
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 0, p: 1 }}>
                            <Grid item xs={4} textAlign="left">
                                <Typography fontWeight={600}>{t('Header in the File')}</Typography>
                            </Grid>
                            <Grid item xs={4} textAlign="left">
                                <Typography fontWeight={600}>{t('Field Name in the System')}</Typography>
                            </Grid>
                            <Grid item xs={4} textAlign="left">
                                <Typography fontWeight={600}>{t('Match Field (for reference)')}</Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: '250px', p: 1, pt: 2, pb: 2 }}>
                            <Grid container spacing={2}>
                                {columns.map((col) => {
                                    const isIdentifierField = importMode === 'update' && col === identifier.fileField;
                                    const mappedFieldName = mapping[col];
                                    const mappedField = fieldDefs[mappedFieldName];
                                    const isReference = mappedField?.type === 'reference';

                                    // Get reference module fields for dropdown
                                    let refModuleFields = [];
                                    if (isReference && mappedField.refModelId) {
                                        const refModuleSections = mappedField.refModelId?.sections || [];
                                        refModuleFields = refModuleSections
                                            .flatMap(sec => sec.fields || [])
                                            .filter(f => !['createdAt', 'updatedAt'].includes(f.name) && f.type !== 'reference')
                                            .map(f => ({ value: f.name, label: f.label || f.name }));
                                    }

                                    return (
                                        <React.Fragment key={col}>
                                            <Grid item xs={4}>
                                                <Typography>
                                                    {col}
                                                    {isIdentifierField && (
                                                        <Typography component="span" sx={{ ml: 1, fontSize: '0.85em', color: 'primary.main' }}>
                                                            ({t('Identifier')})
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <CrmSelectField
                                                    name={col}
                                                    value={mapping[col] ?? ''}
                                                    onChange={(e) => handleMappingChange(col, e.target.value)}
                                                    options={getSystemOptionsForColumn(col)}
                                                    disabled={isIdentifierField}
                                                    sx={{
                                                        height: 35
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                {isReference ? (
                                                    <CrmSelectField
                                                        value={referenceFieldConfigs[mappedFieldName]?.matchField || ''}
                                                        onChange={(e) => {
                                                            setReferenceFieldConfigs(prev => ({
                                                                ...prev,
                                                                [mappedFieldName]: {
                                                                    matchField: e.target.value
                                                                }
                                                            }));
                                                        }}
                                                        options={[
                                                            { value: '', label: `- ${t('Select Field')} -` },
                                                            ...refModuleFields
                                                        ]}
                                                        placeholder={t('Select Field')}
                                                        sx={{ height: 35 }}
                                                    />
                                                ) : (
                                                    <Box sx={{ height: 35 }} />
                                                )}
                                            </Grid>
                                        </React.Fragment>
                                    );
                                })}
                            </Grid>
                        </Box>

                        </Box>
                        {/* Step 4 – Actions */}
                        {!importResult && (
                            <Box sx={{ mt: 'auto' }}>
                                <Divider sx={{ mt: 2, mb: 1 }} />
                                <Box display="flex" justifyContent="flex-end" gap={2} sx={{ pr: 2 }}>
                                    <CrmButton color="crmContainedGray94" onClick={handleSubmit}>
                                        {t('Import')}
                                    </CrmButton>
                                    <CrmButton color="crmOutlinedGray94" onClick={onCancelClickedHandler}>
                                        {t('Cancel')}
                                    </CrmButton>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Help Modal */}
            <Dialog
                open={!!helpModalOpen}
                onClose={() => setHelpModalOpen(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { direction: isRTL ? 'rtl' : 'ltr' }
                }}
            >
                {helpModalOpen && helpContent[helpModalOpen] && (
                    <>
                        <DialogTitle sx={{ textAlign: isRTL ? 'right' : 'left' }}>{helpContent[helpModalOpen].title}</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ whiteSpace: 'pre-line', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                                {helpContent[helpModalOpen].content}
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                            <CrmButton onClick={() => setHelpModalOpen(null)}>
                                {t('Close')}
                            </CrmButton>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default ModuleImportRecords;
